import { useCallback, useState } from "react"

import { useEventBus } from "@cirrus/core/events"
import { useTranslations } from "@cirrus/core/i18n"

import { IProctorioObserverEvent } from "../event"
import { clearObserverTimers, getObserverState } from "../helpers/observerActions"
import { startProctorioSessionOnce } from "../helpers/startProctorioSessionOnce"
import { getPersistedTake, setPersistedTake } from "../helpers/takeExamStorageActions"

export const useProctorioObserver = () => {
  const bus = useEventBus<IProctorioObserverEvent>()
  const [observerInitialized, setObserverInitialized] = useState(false)
  const { i18n } = useTranslations()

  const startProctorioObserver = useCallback(() => {
    const state = getObserverState()
    state.takeExamReceived = state.takeExamReceived || getPersistedTake()

    if (state.started) {
      if (!state.takeExamReceived)
        bus.emit("ProctorioObserver > Dialog > Open", {
          content: i18n("Delivery.ProctorioObserver:ProctoringInitializingMessage")
        })

      setObserverInitialized(true)
      return
    }

    if (state.takeExamReceived) return

    state.started = true

    const runId = Math.random().toString(16).slice(2, 7)
    state.runId = runId

    const ObserveNs = window.Observe
    const ObserveCtor = ObserveNs?.Observe

    if (!ObserveCtor) {
      state.started = false
      state.runId = null
      return
    }

    const instance = new ObserveCtor()

    if (!instance) {
      state.started = false
      state.runId = null
      return
    }

    bus.emit("ProctorioObserver > Dialog > Open", {
      content: i18n("Delivery.ProctorioObserver:ProctoringInitializingMessage")
    })

    instance.takeExam(() => {
      const current = getObserverState()
      if (current.runId !== runId) return

      current.takeExamReceived = true
      setPersistedTake()
      clearObserverTimers()

      bus.emit("ProctorioObserver > Dialog > Open", {
        content: i18n("Delivery.ProctorioObserver:SuccessInitializedMessage")
      })

      const closeId = window.setTimeout(() => {
        bus.emit("ProctorioObserver > Dialog > Close")
      }, CLOSE_TIMEOUT)

      current.timers.push(closeId)
    })

    const stillWaitingTimeout = window.setTimeout(() => {
      const current = getObserverState()
      if (current.runId !== runId) return
      if (current.takeExamReceived) return

      bus.emit("ProctorioObserver > Dialog > Open", {
        content: i18n("Delivery.ProctorioObserver:StillInitializingMessage")
      })

      const retryTimeoutId = window.setTimeout(() => {
        const timeoutState = getObserverState()
        if (timeoutState.runId !== runId) return
        if (!timeoutState.takeExamReceived) startProctorioSessionOnce()
      }, RETRY_DELAY_MS)

      current.timers.push(retryTimeoutId)

      const failMessageTimeoutId = window.setTimeout(() => {
        const timeoutState = getObserverState()
        if (timeoutState.runId !== runId) return
        if (timeoutState.takeExamReceived) return

        bus.emit("ProctorioObserver > Dialog > Open", {
          content: i18n("Delivery.ProctorioObserver:FailureInitializingMessage")
        })
      }, TAKE_EXAM_TIMEOUT_MS)

      current.timers.push(failMessageTimeoutId)

    }, TAKE_EXAM_TIMEOUT_MS)

    setObserverInitialized(true)
    state.timers.push(stillWaitingTimeout)
  }, [i18n])

  return {
    startProctorioObserver,
    observerInitialized
  }
}

const TAKE_EXAM_TIMEOUT_MS = 30000
const RETRY_DELAY_MS = 5000
const CLOSE_TIMEOUT = 5000
